import React, { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import Text from "../Text/Text";
import LanguageIcon from "../Svg/Icons/Language";
import ChevronDownIcon from "../Svg/Icons/ChevronDown";
import CheckmarkIcon from "../Svg/Icons/Checkmark";
import { Language } from "./types";

interface Props {
  currentLang: string;
  langs: Language[];
  setLang: (lang: Language) => void;
}

const shortLabel = (lang: Language) => {
  const locale = lang.locale.toLowerCase();
  if (locale === "zh-cn") return "中";
  if (locale === "zh-tw") return "繁";
  if (locale === "ja-jp") return "JP";
  if (locale === "ko-kr") return "KR";
  return lang.code.slice(0, 2).toUpperCase();
};

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Trigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1px solid ${({ theme, $open }) => ($open ? theme.colors.primary : theme.colors.cardBorder)};
  background: ${({ theme }) => theme.colors.input};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.2s ease, opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary0f};
  }
`;

const RotatingChevron = styled.div<{ $open: boolean }>`
  display: flex;
  color: ${({ theme }) => theme.colors.textSubtle};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  display: block;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: ${({ theme }) => theme.radii.card};
  box-shadow: ${({ theme }) => theme.shadows.level1};
  z-index: ${({ theme }) => theme.zIndices.dropdown};
`;

const Option = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border: none;
  background: ${({ theme, $selected }) => ($selected ? theme.colors.primary0f : "transparent")};
  color: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.text)};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme, $selected }) => ($selected ? theme.colors.primary0f : theme.colors.dropdownDeep)};
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

const ShortCode = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`;

const CheckIcon = styled(CheckmarkIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const LanguageDropdown: React.FC<Props> = ({ currentLang, langs, setLang }) => {
  const [open, setOpen] = useState(false);
  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null);

  useOnClickOutside(
    open ? rootNode : null,
    useCallback(() => setOpen(false), [])
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const current = useMemo(() => langs.find((lang) => lang.code === currentLang) || langs[0], [currentLang, langs]);

  const select = useCallback(
    (lang: Language) => {
      setOpen(false);
      if (lang.code !== currentLang) setLang(lang);
    },
    [currentLang, setLang]
  );

  return (
    <Root ref={setRootNode}>
      <Trigger
        type="button"
        $open={open}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
      >
        <LanguageIcon width="18px" color="textSubtle" />
        <Text color="text">{current?.language}</Text>
        <RotatingChevron $open={open}>
          <ChevronDownIcon />
        </RotatingChevron>
      </Trigger>
      {open && (
        <Dropdown role="listbox" aria-label="Available languages">
          {langs.map((lang) => {
            const selected = lang.code === currentLang;
            return (
              <li key={lang.locale}>
                <Option
                  type="button"
                  role="option"
                  aria-selected={selected}
                  $selected={selected}
                  onClick={() => select(lang)}
                >
                  <span>{lang.language}</span>
                  <RightGroup>
                    <ShortCode>{shortLabel(lang)}</ShortCode>
                    {selected && <CheckIcon />}
                  </RightGroup>
                </Option>
              </li>
            );
          })}
        </Dropdown>
      )}
    </Root>
  );
};

export default React.memo(LanguageDropdown);
